// Screens that save nothing, told to the activity log once (BACKLOG A2,
// `POST /api/me/activity`).
//
// Opening the record book, a draft's recap or a leaguemate's trophy room
// writes nothing, so the server cannot see it; an achievement that asks for
// one needs the client to say so (ACHIEVEMENTS.md §16). Once per feature per
// account: the answer lists every feature the account has reported from any
// device, and this browser keeps that list, so a screen opened a hundred times
// is one request ever.
//
// Never in the way. A failure is silent and tries again the next time the
// screen opens; only a real league counts, not the tour's simulator.

import { useEffect } from 'react';
import { ActivityReported, type ClientFeature } from '@fsp/domain';
import { send } from './api';
import { useLeagueSource } from './leagueSource';
import { useSession } from './session';
import { storageKey } from './storage';

const TRIED_KEY = storageKey('tried.v1');

/** Per account, because two people can share a browser. */
function knownTo(userId: string): readonly string[] {
  try {
    const all = JSON.parse(localStorage.getItem(TRIED_KEY) ?? '{}') as Record<string, unknown>;
    const mine = all[userId];
    return Array.isArray(mine) ? mine.filter((f): f is string => typeof f === 'string') : [];
  } catch {
    return [];
  }
}

function remember(userId: string, tried: readonly ClientFeature[]): void {
  try {
    const all = JSON.parse(localStorage.getItem(TRIED_KEY) ?? '{}') as Record<string, unknown>;
    localStorage.setItem(TRIED_KEY, JSON.stringify({ ...all, [userId]: tried }));
  } catch {
    // Storage blocked: the next open asks again, which writes nothing twice.
  }
}

/** One request at a time per account and feature, however many copies of a screen mount. */
const asking = new Set<string>();

/** Tell the account, once, that this screen was opened. `when` false skips it (my own trophy room). */
export function useTried(feature: ClientFeature, when = true): void {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const real = useLeagueSource() === 'api';

  useEffect(() => {
    if (!when || !real || userId === null) return;
    if (knownTo(userId).includes(feature)) return;
    const key = `${userId}|${feature}`;
    if (asking.has(key)) return;
    asking.add(key);
    void send('POST', '/me/activity', { tried: [feature] }, ActivityReported).then((reply) => {
      asking.delete(key);
      if (reply.status === 'ok') remember(userId, reply.data.tried);
    });
  }, [feature, when, real, userId]);
}
